import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLanguage, SUPPORTED_LANGS, type Lang } from "@/hooks/useLanguage";

const FLAGS: Record<Lang, string> = {
  en: "🇬🇧",
  fr: "🇫🇷",
  de: "🇩🇪",
  es: "🇪🇸",
  pt: "🇵🇹",
};

export default function LanguageSwitcher() {
  const { t } = useTranslation();
  const { currentLang, switchLanguage } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-full border border-background-300/60 bg-background-50/80 px-3 py-1.5 text-sm font-medium whitespace-nowrap cursor-pointer transition hover:bg-background-100"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="text-base leading-none">{FLAGS[currentLang]}</span>
        <span className="hidden sm:inline">{t(`lang.${currentLang}`)}</span>
        <i className={`ri-arrow-down-s-line transition ${open ? "rotate-180" : ""}`}></i>
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-40 overflow-hidden rounded-lg border border-background-200 bg-background-50 py-1 shadow-sm">
          {SUPPORTED_LANGS.map((lang) => (
            <button
              key={lang}
              type="button"
              onClick={() => {
                switchLanguage(lang);
                setOpen(false);
              }}
              className={`flex w-full items-center gap-2 px-3 py-2 text-sm text-left whitespace-nowrap cursor-pointer transition hover:bg-background-100 ${
                lang === currentLang ? "font-semibold text-primary-700" : "text-foreground-700"
              }`}
            >
              <span className="text-base leading-none">{FLAGS[lang]}</span>
              {t(`lang.${lang}`)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}